import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { useCreateUserGroup, type UserGroupCreate } from "@/hooks/use-user-groups";
import { UserGroupForm } from "@/components/user-groups/user-group-form";
import { PageHeader, Breadcrumbs } from "@/components/ui";

export const Route = createFileRoute("/_authenticated/org/settings/groups/new")({
  component: UserGroupNewPage,
});

function UserGroupNewPage() {
  const { t } = useTranslation(["userGroups", "common"]);
  const navigate = useNavigate();
  const createMutation = useCreateUserGroup();
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  return (
    <div className="mx-auto max-w-2xl p-8">
      <Breadcrumbs
        items={[
          {
            label: t("settings.tabs.groups", { ns: "org" }),
            to: "/org/settings",
            search: { tab: "groups" },
          },
          { label: t("form.createTitle") },
        ]}
      />
      <PageHeader title={t("form.createTitle")} subtitle={t("form.createSubtitle")} />
      <div className="mt-6">
        <UserGroupForm
          mode="create"
          isSubmitting={createMutation.isPending}
          submitError={submitError}
          onCancel={() => void navigate({ to: "/org/settings", search: { tab: "groups" } })}
          onSubmit={(body) => {
            setSubmitError(undefined);
            // UserGroupForm's onSubmit emits the shared create|update body union;
            // mode="create" pins it to the create shape, so the cast is sound.
            createMutation.mutate(body as UserGroupCreate, {
              onSuccess: (group) =>
                void navigate({
                  to: "/org/settings/groups/$groupId",
                  params: { groupId: group.id },
                }),
              onError: (err) => {
                // The create op declares 409 ONLY for the name conflict, and its
                // 409 body is a plain ErrorResponse with no `code` discriminator
                // (unlike the PATCH path) — so a 409 status IS the name conflict.
                const isNameConflict =
                  err instanceof ApiError &&
                  (err.status === 409 || err.code === "user_group_name_conflict");
                setSubmitError(
                  isNameConflict ? t("errors.nameConflict") : t("errors.createFailed"),
                );
              },
            });
          }}
        />
      </div>
    </div>
  );
}
